import { GetServerSideProps, NextPage } from 'next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { observer } from 'mobx-react'
import { VerticalNavDefaultPageTemplate } from '@/components/templates/VerticalNavDefaultPageTemplate'
import { ProfileSingleColumnContentTemplate } from '@/components/templates/ProfileSingleColumnContentTemplate'
import { getStaticStores, getUserAvatar, useStores } from '@/utils'
import { IUserBase, Language } from '@/types'
import { MyHead } from '@/components/molecules/MyHead'
import { UserDetailHeader } from '@/components/organisms/UserDetailHeader'
import { PrimaryHeading } from '@/components/atoms/PrimaryHeading'
import { UserReferenceGroup } from '@/components/organisms/UserReferencesGroup'
import { UserProfileExperiences } from '@/components/organisms/UserProfileExperiences'
import { TokenPortfolio } from '@/components/organisms/TokenPortfolio'
import styles from './index.module.scss'

type Props = {
  user: IUserBase
}

const UserInfo: NextPage<Props> = observer(({ user }) => {
  const { users } = useStores()
  const userInstance = users.createUserInstance(user)
  let description = ''
  if (user?.profile.bio) {
    description = `${user?.profile.bio.substring(0, 130)}${user?.profile.bio.length > 130 ? '...' : ''}`
  }

  return (
    <VerticalNavDefaultPageTemplate>
      <MyHead
        title={`${user?.name}'s info`}
        description={description}
        image={getUserAvatar(user, true)}
        twitterCardType='summary'
      />
      <ProfileSingleColumnContentTemplate header={<UserDetailHeader user={userInstance} />} hasHeaderImage>
        {user?.experiences.length > 0 && (
          <div className={styles.content}>
            <UserProfileExperiences user={user} />
          </div>
        )}
        {user?.profile.tokens.length > 0 && (
          <div className={styles.content}>
            <TokenPortfolio user={user} />
          </div>
        )}
        <div className={styles.heading}>
          <PrimaryHeading backgroundText='References'>References</PrimaryHeading>
        </div>
        <div className={styles.content}>
          <UserReferenceGroup user={user} />
        </div>
      </ProfileSingleColumnContentTemplate>
    </VerticalNavDefaultPageTemplate>
  )
})

export const getServerSideProps: GetServerSideProps = async ({ locale, params }) => {
  const { users, viewer } = getStaticStores()
  await viewer.changeLanguage(locale as Language)
  const user = await users.fetchUser({ username: params.id as string, limitOfReference: 100, limitOfQuestion: 1 })

  if (!user) {
    return {
      notFound: true,
    }
  }

  return {
    props: {
      user,
      ...(await serverSideTranslations(locale, ['global', 'companies-users-[id]', 'p', 'validation'])),
    },
  }
}

export default UserInfo
